import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Coins, History as HistoryIcon, Loader2, Coffee, Gift, UserPlus, TrendingUp, Zap, ScanBarcode, Package, Inbox } from 'lucide-react';
import { TierProgressResponse, VenueLoyaltySettingsPublic } from '@lunit/shared-types';
import { useVenue } from '@core/contexts/VenueContext';
import { useUser } from '@core/contexts/ProfileContext';
import { coreRuntime } from '@core/runtime';
import { useIdentity } from '@core/contexts/IdentityContext';
import { getLoyaltySettings } from '@core/api/loyaltySettings';
import { WelcomeScreen } from '@core/components/WelcomeScreen';
import { Card } from '@core/components/Card';
import { Button } from '@core/components/Button';
import { Header } from '@core/components/Header';
import { LoyaltyInfoSheet } from '@core/components/LoyaltyInfoSheet';
import { PhoneLinkBanner } from '@core/components/PhoneLinkBanner';
import { WebPushPrompt } from '@core/components/WebPush';
import { ProfileLoadError } from '@core/components/ProfileLoadError';

export function HomePage() {
  const { features, venue } = useVenue();
  const { user, loading: userLoading, refresh: refreshProfile } = useUser();
  const identity = useIdentity();
  const navigate = useNavigate();
  const [showWelcome, setShowWelcome] = useState(false);
  const [tierProgress, setTierProgress] = useState<TierProgressResponse | null>(null);
  const [loyaltySettings, setLoyaltySettings] = useState<VenueLoyaltySettingsPublic | null>(null);
  const [showLoyaltyInfo, setShowLoyaltyInfo] = useState(false);
  const [onboarded, setOnboarded] = useState<boolean>(() => {
    if (typeof window === 'undefined') {
      return false;
    }
    return localStorage.getItem('mini-app-onboarded') === 'true';
  });

  // 🏆 TRUE LUXURY MODE - Detect premium theme
  const isPremium = venue?.branding?.themePreset?.toLowerCase().includes('premium') ?? false;

  // Show welcome screen for new users
  useEffect(() => {
    if (!user || userLoading) {
      return;
    }

    // Показываем приветственное окно при первом запуске (если не прошёл онбординг)
    if (!onboarded) {
      setShowWelcome(true);
    } else {
      setShowWelcome(false);
    }
  }, [user, userLoading, onboarded]);

  // Load tier progress (как именно — знает шелл: telegramId либо cookie-сессия)
  useEffect(() => {
    if (!user) {
      return;
    }

    const loadTierProgress = async () => {
      try {
        const progress = await coreRuntime().fetchTierProgress(identity);
        setTierProgress(progress);
      } catch (error) {
        console.error('[HomePage] Failed to load tier progress:', error);
      }
    };

    void loadTierProgress();
  }, [user?.id]);

  // Load loyalty settings (for referral bonus amounts)
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const settings = await getLoyaltySettings();
        setLoyaltySettings(settings);
      } catch (error) {
        console.error('[HomePage] Failed to load loyalty settings:', error);
      }
    };

    void loadSettings();
  }, []);

  const handleCardClick = (path: string) => navigate(path);

  // Loyalty level from tier progress API
  const currentTierName = tierProgress?.currentTier?.name || user?.tier || 'Bronze';
  const nextTierName = tierProgress?.nextTier?.name || 'Silver';
  const pointsToNext = tierProgress?.spentToNext || 0;
  const progressToNextLevel = tierProgress?.progress || 0;

  // Referral bonus from loyalty settings
  const referrerAmount = loyaltySettings?.referralBonus?.referrerAmount ?? 0;

  // Next reward calculation (example - should come from rewards API)
  const nextReward = {
    name: 'Капучино',
    pointsNeeded: 150,
    pointsRemaining: Math.max(0, 150 - (user?.balance || 0))
  };

  if (userLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-page text-text-main">
        <Loader2 className="animate-spin text-primary" size={32} />
      </div>
    );
  }

  // K-041: без профиля (ошибка загрузки) не показываем «Гость» и 0 баллов — экран ошибки с «Повторить».
  if (!user) {
    return <ProfileLoadError onRetry={refreshProfile} />;
  }

  if (showWelcome) {
    return (
      <WelcomeScreen
        onStart={() => {
          setShowWelcome(false);
          setOnboarded(true);
          if (typeof window !== 'undefined') {
            localStorage.setItem('mini-app-onboarded', 'true');
          }
        }}
      />
    );
  }

  return (
    <div
      className={`min-h-screen text-text-main pb-20 ${isPremium ? 'bg-[#050505]' : 'bg-page'}`}
    >
      <Header showBack={false} />
      <div className="p-4 space-y-4">
        {/* User Loyalty Status - Compact with Level & Progress */}
        <Card
          className={
            isPremium
              ? 'bg-zinc-900/80 backdrop-blur-md border border-yellow-500/30 !rounded-[24px] overflow-hidden'
              : 'border-2 border-primary/20'
          }
          onClick={() => setShowLoyaltyInfo(true)}
        >
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-text-muted text-xs uppercase tracking-wide mb-1">
                {user?.firstName || 'Гость'}
              </div>
              <div className="flex items-center gap-2">
                <TrendingUp className={`w-4 h-4 ${isPremium ? 'text-yellow-500' : 'text-primary'}`} />
                <span className="font-bold text-text-main">{currentTierName}</span>
              </div>
            </div>
            <div className="text-right">
              <div className="flex items-center gap-2 justify-end">
                <Coins className={`w-5 h-5 ${isPremium ? 'text-yellow-500' : 'text-primary'}`} />
                <span className={`text-3xl font-extrabold ${isPremium ? 'text-yellow-400' : 'text-text-main'}`}>
                  {user?.balance || 0}
                </span>
              </div>
              <div className="text-xs text-text-muted">баллов</div>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="space-y-1">
            <div className="flex justify-between text-xs text-text-muted">
              <span>До {nextTierName}</span>
              <span>
                {Math.round(pointsToNext)}₽ ({Math.round(progressToNextLevel)}%)
              </span>
            </div>
            <div className="h-2 bg-secondary/20 rounded-full overflow-hidden">
              <div
                className={`h-full transition-all duration-500 ${
                  isPremium ? 'bg-gradient-to-r from-yellow-600 to-yellow-400' : 'bg-primary'
                }`}
                style={{ width: `${progressToNextLevel}%` }}
              />
            </div>
          </div>
        </Card>

        {/* Telegram без номера: привязать номер, чтобы входить и через браузер */}
        <PhoneLinkBanner />

        {/* PWA после входа: уведомления в браузере (разрешение — только по нажатию) */}
        <WebPushPrompt />

        {/* 🔥 ГЛАВНЫЙ CTA - Ближайшая награда */}
        {nextReward.pointsRemaining > 0 ? (
          <Card
            className={
              isPremium
                ? 'bg-gradient-to-br from-zinc-900 to-zinc-950 border border-yellow-500/40 !rounded-[24px] overflow-hidden'
                : 'bg-gradient-to-br from-primary/10 to-primary/5 border-2 border-primary/30'
            }
          >
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <span className="text-2xl">🎯</span>
                <div className="text-xs text-text-muted uppercase tracking-wide font-semibold">
                  Ближайшая награда
                </div>
              </div>

              <div className="pl-1">
                <div className="text-xl font-bold text-text-main mb-2">
                  {nextReward.name} — {nextReward.pointsNeeded} баллов
                </div>
                <div className="flex items-center gap-2">
                  <Zap className={`w-5 h-5 ${isPremium ? 'text-yellow-500' : 'text-primary'}`} />
                  <span className="text-base text-text-main">
                    Осталось:{' '}
                    <span className={`font-extrabold text-lg ${isPremium ? 'text-yellow-400' : 'text-primary'}`}>
                      {nextReward.pointsRemaining}
                    </span>{' '}
                    баллов
                  </span>
                </div>
              </div>

              {isPremium ? (
                <button
                  className="w-full mt-2 py-3 px-4 !rounded-[16px] bg-gradient-to-b from-yellow-400 to-yellow-600 text-black font-bold shadow-lg shadow-yellow-500/20 hover:shadow-yellow-500/30 transition-all active:scale-95"
                  onClick={() => handleCardClick('/rewards')}
                >
                  Посмотреть все награды
                </button>
              ) : (
                <Button fullWidth className="mt-2" onClick={() => handleCardClick('/rewards')}>
                  Посмотреть все награды
                </Button>
              )}
            </div>
          </Card>
        ) : (
          <Card
            className={
              isPremium
                ? 'bg-gradient-to-br from-zinc-900 to-zinc-950 border border-yellow-500/40 !rounded-[24px] overflow-hidden'
                : 'bg-gradient-to-br from-success/10 to-success/5 border-2 border-success/30'
            }
          >
            <div className="text-center">
              <div className="text-2xl mb-2">🎉</div>
              <div className="font-bold text-text-main mb-1">У вас достаточно баллов!</div>
              <div className="text-sm text-text-muted mb-3">Обменяйте на награду прямо сейчас</div>
              {isPremium ? (
                <button
                  className="w-full py-3 px-4 !rounded-[16px] bg-gradient-to-b from-yellow-400 to-yellow-600 text-black font-bold shadow-lg shadow-yellow-500/20 hover:shadow-yellow-500/30 transition-all active:scale-95"
                  onClick={() => handleCardClick('/rewards')}
                >
                  Получить награду
                </button>
              ) : (
                <Button fullWidth onClick={() => handleCardClick('/rewards')}>
                  Получить награду
                </Button>
              )}
            </div>
          </Card>
        )}

        {/* 🎁 Referral Banner - Отдельный блок */}
        <Card
          className={
            isPremium
              ? 'bg-zinc-900/80 backdrop-blur-md border border-yellow-500/40 cursor-pointer hover:border-yellow-500/60 transition-all !rounded-[24px] overflow-hidden'
              : 'bg-gradient-to-r from-primary/15 to-secondary/10 border-2 border-primary/40 cursor-pointer'
          }
          onClick={() => handleCardClick('/invite-friend')}
        >
          <div className="flex items-center gap-4">
            <div
              className={`w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0 ${
                isPremium ? 'bg-yellow-500/20' : 'bg-primary/20'
              }`}
            >
              <UserPlus className={`w-7 h-7 ${isPremium ? 'text-yellow-500' : 'text-primary'}`} />
            </div>
            <div className="flex-1">
              <div className="font-bold text-text-main mb-1">Пригласи друга</div>
              <div className="text-sm text-text-muted">
                {referrerAmount > 0 ? (
                  <>
                    Получи{' '}
                    <span className={`font-bold ${isPremium ? 'text-yellow-400' : 'text-primary'}`}>+{referrerAmount} баллов</span> за
                    каждого друга
                  </>
                ) : (
                  'Приглашай друзей и получай бонусы'
                )}
              </div>
            </div>
            <div className="text-2xl">→</div>
          </div>
        </Card>

        {/* 📦 Абонементы Banner */}
        {features?.bundlesEnabled && (
          <Card
            className={
              isPremium
                ? 'bg-zinc-900/80 backdrop-blur-md border border-yellow-500/40 cursor-pointer hover:border-yellow-500/60 transition-all !rounded-[24px] overflow-hidden'
                : 'bg-gradient-to-r from-primary/15 to-secondary/10 border-2 border-primary/40 cursor-pointer'
            }
            onClick={() => handleCardClick('/bundles')}
          >
            <div className="flex items-center gap-4">
              <div
                className={`w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0 ${
                  isPremium ? 'bg-yellow-500/20' : 'bg-primary/20'
                }`}
              >
                <Package className={`w-7 h-7 ${isPremium ? 'text-yellow-500' : 'text-primary'}`} />
              </div>
              <div className="flex-1">
                <div className="font-bold text-text-main mb-1">Абонементы</div>
                <div className="text-sm text-text-muted">
                  Пакеты напитков по выгодной цене
                </div>
              </div>
              <div className="text-2xl">→</div>
            </div>
          </Card>
        )}

        {/* Quick Actions Grid - 2x2 layout */}
        <div className="grid grid-cols-2 gap-4">
          {/* Row 1: Меню, История */}
          {features?.menuPublished && (
            <Card
              className={
                isPremium
                  ? 'text-center cursor-pointer bg-zinc-900/60 backdrop-blur-sm border border-zinc-800 hover:border-yellow-500/50 transition-all !rounded-[24px] overflow-hidden'
                  : 'text-center cursor-pointer hover:border-primary/40 transition-colors'
              }
              onClick={() => {
                if (features?.externalMenuUrl) {
                  window.open(features.externalMenuUrl, '_blank');
                } else {
                  handleCardClick('/menu');
                }
              }}
            >
              <Coffee className={`w-8 h-8 mx-auto mb-2 ${isPremium ? 'text-yellow-500' : 'text-primary'}`} />
              <div className="font-medium text-text-main">Меню</div>
            </Card>
          )}

          <Card
            className={
              isPremium
                ? 'text-center cursor-pointer bg-zinc-900/60 backdrop-blur-sm border border-zinc-800 hover:border-yellow-500/50 transition-all !rounded-[24px] overflow-hidden'
                : 'text-center cursor-pointer hover:border-primary/40 transition-colors'
            }
            onClick={() => handleCardClick('/history')}
          >
            <HistoryIcon className={`w-8 h-8 mx-auto mb-2 ${isPremium ? 'text-yellow-500' : 'text-primary'}`} />
            <div className="font-medium text-text-main">История</div>
          </Card>

          {/* Row 2: Акции, Моя карта */}
          {features?.promotionsPublished && (
            <Card
              className={
                isPremium
                  ? 'text-center cursor-pointer bg-zinc-900/60 backdrop-blur-sm border border-zinc-800 hover:border-yellow-500/50 transition-all !rounded-[24px] overflow-hidden'
                  : 'text-center cursor-pointer hover:border-primary/40 transition-colors'
              }
              onClick={() => handleCardClick('/promotions')}
            >
              <Gift className={`w-8 h-8 mx-auto mb-2 ${isPremium ? 'text-yellow-500' : 'text-primary'}`} />
              <div className="font-medium text-text-main">Акции</div>
            </Card>
          )}

          <Card
            className={
              isPremium
                ? 'text-center cursor-pointer bg-zinc-900/60 backdrop-blur-sm border border-zinc-800 hover:border-yellow-500/50 transition-all !rounded-[24px] overflow-hidden'
                : 'text-center cursor-pointer hover:border-primary/40 transition-colors'
            }
            onClick={() => handleCardClick('/qr-code')}
          >
            <ScanBarcode className={`w-8 h-8 mx-auto mb-2 ${isPremium ? 'text-yellow-500' : 'text-primary'}`} />
            <div className="font-medium text-text-main">Моя карта</div>
          </Card>

          {/* Подарки */}
          {features?.giftsEnabled && (
            <Card
              className={
                isPremium
                  ? 'text-center cursor-pointer bg-zinc-900/60 backdrop-blur-sm border border-zinc-800 hover:border-yellow-500/50 transition-all !rounded-[24px] overflow-hidden'
                  : 'text-center cursor-pointer hover:border-primary/40 transition-colors'
              }
              onClick={() => handleCardClick('/gifts')}
            >
              <Inbox className={`w-8 h-8 mx-auto mb-2 ${isPremium ? 'text-yellow-500' : 'text-primary'}`} />
              <div className="font-medium text-text-main">Подарки</div>
            </Card>
          )}
        </div>
      </div>

      <LoyaltyInfoSheet
        isOpen={showLoyaltyInfo}
        onClose={() => setShowLoyaltyInfo(false)}
      />
    </div>
  );
}
